import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  Dialog,
  IconButton,
  makeStyles,
  Typography,
  InputBase,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { AutoCompleteSelect, AutoCompleteInput } from '../../../../../components';
import { CloseIcon } from '../../../../../components/Icon';
import { i18n } from '../../../../../language';
import { userRoleList } from '../../../../../utils/userRole';
import { hospitalWardListFunc } from '../../../../../utils/hospitalWardList';
import { roleIdE, roleWeight } from '../../../../../comm/RoleMap';
import { systemAPI } from '../../../../../apis';
import { classNames, initValue } from '../../../../../utils';
import { checkSubmitData } from '../../../../../utils/check';
import MyAlert from '../../../../../components/Alert';
import { IS_MOCK_API } from '../../../../../comm/config';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiPaper-root': {
      padding: '0',
      margin: '0',
      boxShadow: 'unset',
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '43.75rem',
    borderRadius: '0.625rem',
    boxShadow: '0 -2px 21px 0 rgba(30, 56, 140, 0.18)',
    backgroundColor: '#fff',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2.375rem 1.875rem 1.563rem 1.563rem',
  },
  headerTitle: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2e4469',
  },
  closeIcon: {
    fontSize: '1.375rem',
    color: '#538fff',
    margin: '-12px -12px 0 0',
  },
  label: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#7d86a9',
    marginBottom: '0.625rem',
  },
  main: {
    padding: '0 1.563rem',
    overflow: 'auto',
  },
  InputBase: {
    height: '3.5rem',
    borderRadius: '0.625rem',
    border: 'solid 1px #e4e7eb',
    width: '100%',
    paddingLeft: '1.25rem',
    // marginBottom: '1.875rem',
    '& .MuiInputBase-input': {
      fontSize: '1rem',
      color: '#2e4469',
      fontFamily: 'NotoSans-Medium',
    },
  },
  cancelBtn: {
    width: '12.5rem',
    height: '3rem',
    borderRadius: '0.625rem',
    border: 'solid 1.5px #538fff',
    color: '#538fff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    background: '#f5faff',
  },
  configBtn: {
    width: '12.5rem',
    height: '3rem',
    borderRadius: '0.625rem',
    background: '#538fff',
    color: '#fff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
  },
  disabled: {
    background: '#f5f5f5',
    '& .MuiInputBase-input': {
      color: '#A0ABBA',
    },
  },
  labelError: {
    color: '#f44336',
  },
  inputError: {
    border: '1px solid #f44336',
  },
  errorMsg: {
    margin: '3px 0.825rem 0 0.825rem',
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
  verify: {
    fontSize: '1rem',
    fontWeight: '500',
    color: '#538fff',
    padding: '0.825rem',
  },
  alert: {
    '& .MuiAlert-standardSuccess': {
      // minWidth: '10rem',
      padding: '0.2rem 1rem',
    },
  },
  checkBox: {
    marginLeft: '0rem',
    '& .MuiButtonBase-root': {
      padding: '0',
      paddingRight: '0.938rem',
    },
    '& .MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '& .MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1.125rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '& .Mui-checked': {
      color: '#28d2ab',
    },
  },
  disabledCheckBox: {
    '& .MuiButtonBase-root': {
      color: '#c0c6cb',
    },
    '& .MuiTypography-root': {
      color: '#2e446988',
    },
  },
}));

export default function User({ closePop, open, title, allData, confirmClick, confirmText, itemLists }) {
  const loginInfo = useSelector((state) => state.loginInfo);
  const userRole = useSelector((state) => state.userRole);
  const classes = useStyles();

  // input
  const [inputValues, setInputValues] = useState({
    userId: initValue(allData?.userId),
    corpId: initValue(allData?.corpId),
    roleId:
      allData?.roleId ??
      userRoleList.getRoleType(userRole, loginInfo.hospitalId, loginInfo.wardId, loginInfo.userId) ??
      -1,
    status: allData?.status === 0 ? 0 : 1,
    wardId: allData?.wardId ?? -1,
    hospitalId: allData?.hospId ?? -1,
    staffName: initValue(allData?.staffName),
    type: allData ? allData?.type : 2,
    byPassHaChat: allData?.byPassHaChat ?? 0,
    // wardId:
    //   allData?.wardId ?? (!allData && (loginInfo.wardId ?? itemLists.hospitalWardList?.[0]?.wards?.[0]?.wardId)) ?? -1,
    // hospitalId:
    //   allData?.hospId ?? (!allData && (loginInfo.hospitalId ?? itemLists.hospitalWardList?.[0]?.hospitalId)) ?? -1,
  });
  const currentRoleTypeId = _.find(itemLists.roleName, (item) => item?.value === inputValues?.roleId)?.roleTypeId;

  const schema = {
    corpId: Yup.string().required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('SETTING_CORP_ID') })),
    staffName: Yup.string().required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('SETTING_STAFF_NAME') })),
    roleId: Yup.number()
      .min(0, i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('SETTING_ROLE_NAME') }))
      .required(),
    status: Yup.number()
      .min(0, i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('SETTING_STATUS') }))
      .required(),
    hospitalId:
      inputValues.hospitalId === null
        ? null
        : Yup.number()
            .typeError(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('SETTING_HOSPITAL_NAME') }))
            .min(0, i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('SETTING_HOSPITAL_NAME') }))
            .required(),
    wardId:
      // HA和PRO Admin 不需要选ward
      inputValues.wardId === null || currentRoleTypeId === 2 || currentRoleTypeId === 7
        ? null
        : Yup.number()
            .typeError(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('SETTING_WARD_NAME') }))
            .min(0, i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('SETTING_WARD_NAME') }))
            .required(),
  };

  const hospitalWardList = useMemo(() => {
    const { hospitalWardList } = itemLists;
    return Array.isArray(hospitalWardList) ? hospitalWardList : [];
  }, [itemLists.hospitalWardList, userRole, allData?.hospitalId]);

  const inputChange = (name, type) => {
    return async (data) => {
      if (type === 'text') {
        setInputValues({ ...inputValues, [name]: data.target.value });
      } else if (type === 'select') {
        if (name === 'hospitalId' && inputValues?.wardId !== null) {
          setInputValues({ ...inputValues, [name]: data, wardId: -1, type: 2 });
        } else {
          setInputValues({ ...inputValues, [name]: data, type: 2 });
        }
      } else if (type === 'radio') {
        setInputValues({ ...inputValues, [name]: data });
      } else if (type === 'srs' || type === 'byPassHaChat') {
        console.log(inputValues);
        setInputValues({ ...inputValues, [name]: inputValues[name] === 1 ? 2 : 1 });
      }
    };
  };

  const focusCheck = (name) => {
    return async () => {
      try {
        await checkSubmitData(Yup.object().shape({ [name]: schema[name] }), { [name]: inputValues[name] });
        setErrorMsg({ ...errorMsg, [name]: null });
      } catch (e) {
        setErrorMsg({ ...errorMsg, ...e });
      }
    };
  };

  // button
  const [errorMsg, setErrorMsg] = useState(null);
  const confirmHandle = async () => {
    try {
      await checkSubmitData(Yup.object().shape(schema), inputValues);
      setErrorMsg(null);
      const iscorpIdVerify = await postVerify();
      if (iscorpIdVerify) {
        if (typeof confirmClick === 'function') {
          confirmClick({
            ...inputValues,
            wardId: inputValues.wardId === -1 ? null : inputValues.wardId,
            byPassHaChat: inputValues.byPassHaChat === 1,
          });
        }
      }
    } catch (errorMsg) {
      setErrorMsg(errorMsg);
    }
  };

  const curAndRoleTypeCompare = () => {
    return (
      roleWeight[allData.roleId] >
      roleWeight[userRoleList.getRoleType(userRole, loginInfo.hospitalId, loginInfo.wardId, loginInfo.userId)]
    );
  };

  const getRoleNames = () => {
    const roleType = userRoleList.getRoleType(userRole, loginInfo.hospitalId, loginInfo.wardId, loginInfo.userId);
    return (allData && curAndRoleTypeCompare()) || userRoleList.isSuperAdminUseGlobleTypeId()
      ? itemLists.roleName
      : _.filter(itemLists.roleName, (item) => {
          return roleWeight[roleType] >= roleWeight[item.value];
        });
  };

  const confirmCtr = () => {
    const flag = allData && curAndRoleTypeCompare();
    return (
      <ButtonBase
        id="setting_user_pop_confirm"
        className={classes.configBtn}
        onClick={() => {
          (async () => {
            await confirmHandle();
          })();
        }}
        disabled={flag}
        style={flag ? { background: '#C0C6CB' } : null}
      >
        {confirmText}
      </ButtonBase>
    );
  };

  const getInputRoleWeight = (weight) => {
    const roleTypeId =
      _.find(itemLists.RoleList, (item) => {
        return inputValues.roleId === item.roleId;
      })?.roleTypeId ?? -1;
    if (roleTypeId > -1) {
      return roleWeight[roleTypeId];
    }
    return -1;
  };

  useEffect(() => {
    const weight = getInputRoleWeight();
    const newInputValues = { ...inputValues };
    const newErroeMsg = {};
    if (weight > roleWeight[roleIdE.HA]) {
      newInputValues.hospitalId = null;
      newErroeMsg.hospitalId = null;
    } else if (newInputValues.hospitalId === null) {
      newInputValues.hospitalId = -1;
    }

    if (weight > roleWeight[roleIdE.WA] || currentRoleTypeId === 7) {
      newInputValues.wardId = null;
      newErroeMsg.wardId = null;
    } else if (newInputValues.wardId === null) {
      newInputValues.wardId = -1;
    }

    setInputValues(newInputValues);
    errorMsg && setErrorMsg({ ...errorMsg, ...newErroeMsg });
  }, [inputValues.roleId, currentRoleTypeId]);

  const getCheckLableClasses = (key) => {
    return errorMsg?.[key] ? classNames(classes.label, classes.labelError) : classes.label;
  };

  const getCheckInputClasses = (key, cls) => {
    return errorMsg?.[key]
      ? classNames(classes.InputBase, classes.inputError, cls)
      : classNames(classes.InputBase, cls);
  };

  const postVerify = async (isAlert) => {
    if (IS_MOCK_API || (allData && inputValues?.status === 0)) return true;
    let rsl = false;
    try {
      const { result: data } = await systemAPI.postCorpIdVerify({ corpId: inputValues?.corpId });
      const result = data?.verify;
      if (!result) {
        setErrorMsg({ ...errorMsg, corpId: i18n.t('SETTING_ACCOUNT_NOT_FOUND_PLEASE_CHECK_AND_TRY_AGAIN') });
      } else {
        setErrorMsg({ ...errorMsg, corpId: null });
        setInputValues({ ...inputValues, staffName: data?.displayName ?? '' });
        isAlert && showMyAlert();
      }
      rsl = result;
    } catch (e) {
      setErrorMsg({ ...errorMsg, corpId: i18n.t('SETTING_ACCOUNT_NOT_FOUND_PLEASE_CHECK_AND_TRY_AGAIN') });
    }
    return rsl;
  };

  const [isMyAlert, setIsMyAlert] = useState(false);
  const closeMyAlert = () => {
    setIsMyAlert(false);
  };
  const showMyAlert = () => {
    setIsMyAlert(true);
    setTimeout(closeMyAlert, 1500);
  };

  const flagHandle = (name, flagKey) => {
    return () => {
      (async () => {
        allData && (await inputChange(name, flagKey)());
      })();
    };
  };

  return (
    <Dialog open={open} maxWidth={false} className={classes.dialog}>
      <MyAlert
        className={classes.alert}
        open={isMyAlert}
        text={i18n.t('SETTING_VERIFY_SUCCESSFUL')}
        type="success"
        onClose={closeMyAlert}
      />
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>{title}</Typography>
          <IconButton id="setting_user_pop_close" className={classes.closeIcon} onClick={closePop}>
            <CloseIcon />
          </IconButton>
        </div>
        <main className={classes.main}>
          <Box display="flex" mb="1.875rem">
            <Box width="50%" mr="1.25rem">
              <Typography className={getCheckLableClasses('corpId')}>{i18n.t('SETTING_CORP_ID')}</Typography>
              <Box
                display="flex"
                border={`solid 1px ${errorMsg?.corpId ? '#f44336' : '#e4e7eb'}`}
                borderRadius="0.625rem"
              >
                <InputBase
                  id="setting_user_pop_corpid"
                  value={inputValues.corpId}
                  className={
                    allData ? getCheckInputClasses('corpId', classes.disabled) : getCheckInputClasses('corpId')
                  }
                  placeholder={i18n.t('SETTING_CORP_ID')}
                  onChange={(event) => {
                    (async () => {
                      await inputChange('corpId', 'text')(event);
                    })();
                  }}
                  disabled={Boolean(allData)}
                  onBlur={(event) => {
                    (async () => {
                      await focusCheck('corpId')(event);
                    })();
                  }}
                  style={{ border: 'unset' }}
                />
                {!allData ? (
                  <ButtonBase
                    id="setting_user_pop_verify"
                    onClick={() => {
                      (async () => {
                        await postVerify(true);
                      })();
                    }}
                    className={classes.verify}
                    disabled={!inputValues.corpId}
                  >
                    {i18n.t('SETTING_VERIFY')}
                  </ButtonBase>
                ) : (
                  ''
                )}
              </Box>
              {errorMsg?.corpId ? <Typography className={classes.errorMsg}>{errorMsg.corpId}</Typography> : ''}
            </Box>
            <Box width="50%">
              <Typography className={getCheckLableClasses('staffName')}>{i18n.t('SETTING_STAFF_NAME')}</Typography>
              <InputBase
                id="setting_user_pop_staffname"
                value={inputValues.staffName}
                className={getCheckInputClasses('staffName')}
                placeholder={i18n.t('SETTING_STAFF_NAME')}
                onChange={(event) => {
                  (async () => {
                    await inputChange('staffName', 'text')(event);
                  })();
                }}
                onBlur={() => {
                  (async () => {
                    await focusCheck('staffName')();
                  })();
                }}
              />
              {errorMsg?.staffName ? <Typography className={classes.errorMsg}>{errorMsg.staffName}</Typography> : ''}
            </Box>
          </Box>
          <Box display="flex" mb="1.875rem">
            <Box width="50%" mr="1.25rem">
              <Typography className={getCheckLableClasses('hospitalId')}>{i18n.t('SETTING_HOSPITAL_NAME')}</Typography>
              <AutoCompleteInput
                id="setting_user_pop_hospitalname"
                value={inputValues.hospitalId}
                placeholder={i18n.t('SETTING_HOSPITAL_NAME')}
                onChange={(data) => {
                  (async () => {
                    await inputChange('hospitalId', 'select')(data);
                  })();
                }}
                itemList={hospitalWardListFunc.getAllHospitalSelectFormat(hospitalWardList)}
                disabled={inputValues.hospitalId === null}
                error={Boolean(errorMsg?.hospitalId)}
                errorMsg={errorMsg?.hospitalId}
                onBlur={focusCheck('hospitalId')}
              />
            </Box>
            <Box width="50%">
              <Typography className={getCheckLableClasses('wardId')}>{i18n.t('SETTING_WARD_NAME')}</Typography>
              <AutoCompleteInput
                id="setting_user_pop_wardname"
                value={inputValues.wardId}
                placeholder={i18n.t('SETTING_WARD_NAME')}
                onChange={inputChange('wardId', 'select')}
                itemList={hospitalWardListFunc.getHospitalAllWardUseIdFormat(hospitalWardList, inputValues.hospitalId)}
                disabled={inputValues.wardId === null}
                error={Boolean(errorMsg?.wardId)}
                errorMsg={errorMsg?.wardId}
                onBlur={focusCheck('wardId')}
              />
            </Box>
          </Box>
          <Box display="flex" mb="1.875rem">
            <Box width="50%" mr="1.25rem">
              <Typography className={getCheckLableClasses('status')}>{i18n.t('SETTING_STATUS')}</Typography>
              <AutoCompleteSelect
                id="setting_user_pop_status"
                value={inputValues.status}
                placeholder={i18n.t('SETTING_STATUS')}
                onChange={inputChange('status', 'select')}
                itemList={itemLists.status}
                error={Boolean(errorMsg?.status)}
                errorMsg={errorMsg?.status}
                onBlur={focusCheck('status')}
              />
            </Box>
            <Box width="50%">
              <Typography className={getCheckLableClasses('roleId')}>{i18n.t('SETTING_ROLE_NAME')}</Typography>
              <AutoCompleteSelect
                id="setting_user_pop_rolename"
                value={inputValues.roleId}
                placeholder={i18n.t('SETTING_ROLE_NAME')}
                onChange={inputChange('roleId', 'select')}
                itemList={itemLists.roleName}
                error={Boolean(errorMsg?.roleId)}
                errorMsg={errorMsg?.roleId}
                onBlur={focusCheck('roleId')}
              />
            </Box>
          </Box>
          {allData ? (
            <Box mb="1.5rem">
              <FormControlLabel
                id="setting_user_pop_srsupdate"
                control={<Checkbox />}
                label={i18n.t('SETTING_SRS_UPDATE')}
                className={classes.checkBox}
                checked={Boolean(inputValues.type === 1)}
                onChange={flagHandle('type', 'srs')}
              />
            </Box>
          ) : (
            ''
          )}
          <Box mb="1.5rem">
            <FormControlLabel
              id="setting_user_pop_bypasshachat"
              control={<Checkbox />}
              label={i18n.t('SETTING_USER_BY_PASS_HA_CHAT')}
              className={classes.checkBox}
              checked={Boolean(inputValues.byPassHaChat === 1)}
              onChange={inputChange('byPassHaChat', 'byPassHaChat')}
            />
          </Box>
        </main>
        <Box display="flex" justifyContent="center" style={{ margin: '1.25rem 0 1.875rem 0' }}>
          <ButtonBase
            id="setting_user_pop_cancel"
            className={classes.cancelBtn}
            style={{ marginRight: '1.313rem' }}
            onClick={closePop}
          >
            {i18n.t('SETTING_CANCEL')}
          </ButtonBase>
          {confirmCtr()}
        </Box>
      </div>
    </Dialog>
  );
}
